import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import LeftArrowIcon from "@redotech/redo-icons/arrow-left.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import {
  AbTestState,
  TreatmentState,
} from "@redotech/redo-return-ui/flows/return-flow/ab-test";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  ButtonSize,
  IconButton,
  IconButtonTheme,
} from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo } from "react";
import * as abtreatmentCss from "./ab-test.module.css";
import { RequiredMessage } from "./required-message";

export const AbTestDetails = memo(function AbTestDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<AbTestState>) {
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const handleAdd = useHandler(() =>
    setState((state) => ({
      ...state,
      treatments: [
        ...state.treatments,
        { name: "", next: undefined, percentage: 0 },
      ],
    })),
  );
  const handleFocus = useHandler((focused: boolean) => {
    if (!focused) {
      setState((state) => ({ ...state, attempted: true }));
    }
  });
  return (
    <>
      <Text fontSize="xs" textColor="secondary">
        Name
      </Text>
      {!state.name && (
        <Text fontSize="xs" fontWeight="regular" textColor="error">
          Required
        </Text>
      )}
      <RedoTextInput
        setValue={(name) => setState((state) => ({ ...state, name }))}
        value={state.name}
      />
      <Flex dir="column" gap="md">
        <IterableMap items={state.treatments} keyFn={(_, index) => index}>
          {(treatment, index) => (
            <StateTreatmentComponent
              canEditReturnsSettings={canEditReturnsSettings}
              handleFocus={handleFocus}
              index={index}
              setState={setState}
              state={state}
              stepSelect={stepSelect}
              treatment={treatment}
            />
          )}
        </IterableMap>
      </Flex>
      <Flex pt="md">
        <RedoButton
          disabled={!canEditReturnsSettings}
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={handleAdd}
          size="sm"
          text="Add treatment"
        />
      </Flex>
      {!state.treatments.length && (
        <RequiredMessage>Must have at least one treatment</RequiredMessage>
      )}
    </>
  );
});

const StateTreatmentComponent = memo(function StateTreatmentComponent({
  state,
  setState,
  stepSelect,
  handleFocus,
  canEditReturnsSettings,
  treatment,
  index,
}: {
  state: StepTypeDetailsProps<AbTestState>["state"];
  setState: StepTypeDetailsProps<AbTestState>["setState"];
  stepSelect: StepTypeDetailsProps<AbTestState>["stepSelect"];
  handleFocus(focused: boolean): void;
  canEditReturnsSettings: boolean;
  treatment: TreatmentState;
  index: number;
}) {
  const setName = useHandler((name: string) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.treatments[index].name = name;
      }),
    ),
  );
  const setPercentage = useHandler((percentage: string) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.treatments[index].percentage = Number(percentage);
      }),
    ),
  );
  const setStep = useHandler((next: StepId | undefined) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.treatments[index].next = next;
      }),
    ),
  );
  const handleUp = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const treatments = state.treatments.splice(index, 1);
        state.treatments.splice(index - 1, 0, ...treatments);
      }),
    );
  });
  const handleDown = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const treatments = state.treatments.splice(index, 1);
        state.treatments.splice(index + 1, 0, ...treatments);
      }),
    );
  });
  const handleDelete = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        state.treatments.splice(index, 1);
      }),
    );
  });
  return (
    <Flex className={abtreatmentCss.treatmentCard} dir="column">
      <Flex align="center" gap="xs" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          Treatment #{index + 1}
        </Text>
        {canEditReturnsSettings && (
          <Flex>
            <IconButton
              disabled={!index}
              onClick={handleUp}
              size={ButtonSize.SMALL}
            >
              <LeftArrowIcon className={abtreatmentCss.upArrow} />
            </IconButton>
            <IconButton
              disabled={index === state.treatments.length - 1}
              onClick={handleDown}
              size={ButtonSize.SMALL}
            >
              <LeftArrowIcon className={abtreatmentCss.downArrow} />
            </IconButton>
            <IconButton
              onClick={handleDelete}
              size={ButtonSize.SMALL}
              theme={IconButtonTheme.DANGER}
            >
              <TrashIcon />
            </IconButton>
          </Flex>
        )}
      </Flex>
      <Text fontSize="xs" textColor="secondary">
        Name
      </Text>
      {!treatment.name && (
        <Text fontSize="xs" fontWeight="regular" textColor="error">
          Required
        </Text>
      )}
      <RedoTextInput
        errorText={!treatment.name ? "Required" : undefined}
        onBlur={(event) => {
          if (!treatment.name) {
            handleFocus(false);
          }
        }}
        onFocus={(event) => {
          if (!treatment.name) {
            handleFocus(false);
          }
        }}
        setValue={setName}
        state={canEditReturnsSettings ? "default" : "readonly"}
        value={treatment.name}
      />
      <Text fontSize="xs" textColor="secondary">
        Percentage
      </Text>
      {!treatment.percentage && (
        <Text fontSize="xs" fontWeight="regular" textColor="error">
          Required
        </Text>
      )}
      <RedoTextInput
        errorText={!treatment.percentage ? "Required" : undefined}
        max={100}
        min={0}
        onBlur={(event) => {
          if (!treatment.percentage) {
            handleFocus(false);
          }
        }}
        onFocus={(event) => {
          if (!treatment.percentage) {
            handleFocus(false);
          }
        }}
        setValue={setPercentage}
        state={canEditReturnsSettings ? "default" : "readonly"}
        value={String(treatment.percentage)}
      />
      <Text fontSize="xs" textColor="secondary">
        Next step
      </Text>
      {!treatment.next && (
        <Text fontSize="xs" fontWeight="regular" textColor="error">
          Required
        </Text>
      )}
      {stepSelect({
        value: treatment.next,
        handleIndex: index,
        valueChange: setStep,
      })}
    </Flex>
  );
});
